import {
	AfterViewInit,
	Component,
	ElementRef,
	HostListener,
	OnDestroy,
	output,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import _ from 'lodash-es';
import {
	Asset,
	AssetKey,
	DeliverablePartner,
	getAssetUpdateOptions,
	getUpdateablAssetFields,
	LocalizedTextIds,
} from 'company-finder-common';
import { EditablePropertyGroup } from '../../company-update.interface.js';
import {
	CompanyService,
	DeploymentContext,
	NewItemPrefix,
	RequireMoreThanJustSpaces,
} from '@Common';
import { ReviewEditsService } from '../../services/review-edits.service.js';
import { CompanyUpdateService } from '../../services/company-update.service.js';
import { UpdateComponentBaseWithEditItems } from '../../UpdateComponentBaseWithEditItems.js';
import { v4 as uuidv4 } from 'uuid';

@Component({
	selector: 'asset-update',
	templateUrl: './asset-update.component.html',
	styleUrls: ['./asset-update.component.scss'],
	standalone: false,
})
export class AssetUpdateComponent
	extends UpdateComponentBaseWithEditItems
	implements OnDestroy, AfterViewInit
{
	public onRoleChange(event): void {
		this.asset.deliverablePartners.find(
			(partner) => partner.partnerId === this.partnerToEdit
		).role = event.target.value;
		this.updateAsset();
	}
	public onNameChange(event): void {
		const name =
			event.target.value?.length > 0 ? event.target.value : 'Unknown';
		this.asset.deliverablePartners.find(
			(partner) => partner.partnerId === this.partnerToEdit
		).partnerName = name;
		this.updateAsset();
	}
	public assetForm: UntypedFormGroup;
	public assetFormSubscription: Subscription;
	public partnerToEdit: string = null;

	public get asset(): Asset {
		return this.updateService.assetToModify;
	}
	public set asset(value: Asset) {
		this.updateService.assetToModify = value;
	}

	public onCloseTab = output<void>();

	@HostListener('window:click')
	protected onClick(): void {
		// Commit any open edit items
		this.commitOpenEditItems();
	}

	constructor(
		deploymentContext: DeploymentContext,
		private reviewEditsService: ReviewEditsService,
		companyService: CompanyService,
		updateService: CompanyUpdateService,
		private elementRef: ElementRef
	) {
		super(deploymentContext, updateService, companyService);
		this.initializeAssetForm();
	}

	public ngAfterViewInit(): void {
		this._deploymentContext.scrollVertical(
			scrollY + this.elementRef.nativeElement.getBoundingClientRect().top
		);
		for (const editItemComponent of this.editItemArray) {
			this.addSubscription(
				editItemComponent.makeEditableSubject.subscribe(
					(payload: { propertyName: string; ev: MouseEvent }) => {
						this.makePropertyEditable(
							payload.propertyName,
							payload.ev
						);
					}
				)
			);
		}
	}

	public initializeAssetForm(): void {
		this.assetFormSubscription?.unsubscribe();
		const controls = {};

		for (const updatableField of getUpdateablAssetFields().filter(
			(field) => !this.isFieldDisabled(field)
		)) {
			const { required } = getAssetUpdateOptions(updatableField);

			controls[updatableField] = new UntypedFormControl(
				this.asset[updatableField],
				required ? RequireMoreThanJustSpaces.required : null
			);
		}

		this.assetForm = new UntypedFormGroup(controls);

		this.assetFormSubscription = this.assetForm.valueChanges.subscribe(
			(formValues) => {
				this.asset = {
					...this.asset,
					...formValues,
				};
				this.updateAsset();
			}
		);
	}

	public updateAsset() {
		const index = this.company.assets.findIndex(
			(asset) => asset.assetId === this.asset.assetId
		);

		this.company.assets[index] = this.asset;
		_.merge(this.companyWithMostRecentEdit.assets, this.company.assets);
		this.companyWithMostRecentEdit.assets[
			this.companyWithMostRecentEdit.assets.findIndex(
				(asset) => asset.assetId === this.asset.assetId
			)
		].deliverablePartners = this.asset.deliverablePartners;
		this.updateService.checkUpdatesForUI();
	}

	public get deliverablePartnersTitle(): string {
		return this.Loc(this.LocalizedTextIds.DeliverablePartner);
	}

	private isFieldDisabled(field: AssetKey): boolean {
		/*	switch (field) {
			case 'keywords':
				return true;
		}*/
		return false;
	}

	public ngOnDestroy(): void {
		this.assetFormSubscription?.unsubscribe();
	}

	public closeTab(): void {
		this.onCloseTab.emit();
	}

	public declineAsset(): void {
		this.reviewEditsService.declineAsset(this.asset);
		this.closeTab();
	}

	public approveAsset(): void {
		this.reviewEditsService.approveAsset(this.asset);
		this.closeTab();
	}

	public updateableProperties: AssetKey[] = getUpdateablAssetFields().filter(
		(field) => !this.isFieldDisabled(field)
	);

	public get propertyGroup(): EditablePropertyGroup {
		return {
			header: this.LocalizedTextIds.Asset,
			properties: this.updateableProperties,
		};
	}

	public get roleLabel(): string {
		return this.Loc(LocalizedTextIds.Role);
	}

	public get nameLabel(): string {
		return this.Loc(LocalizedTextIds.Name);
	}

	public makePropertyEditable(
		propertyName: string,
		ev?: UIEvent,
		consumeEvent?: boolean
	): void {
		if (consumeEvent) {
			this.consumeEvent(ev);
		}

		if (!this.isEditingProperty[propertyName]) {
			this.commitOpenEditItems();
			this.isEditingProperty[propertyName] = true;
			this.reviewEditsService.currentEditItemProperty = propertyName;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} else if ((ev?.target as any).name === 'edit-button') {
			this.itemEditDone(propertyName);
			return;
		}

		setTimeout(() => {
			const element = document.getElementById(
				`company-update-${propertyName}`
			);
			if (element) {
				element.focus();
			}
		}, 300);
	}

	public get isEditingProperty(): { [propName: string]: boolean } {
		return this.updateService.isEditingProperty;
	}

	public commitOpenEditItems(): void {
		for (const key in this.isEditingProperty) {
			if (
				Object.prototype.hasOwnProperty.call(
					this.isEditingProperty,
					key
				)
			) {
				if (this.isEditingProperty[key]) {
					this.itemEditDone(key);
				}
			}
		}
		this.partnerToEdit = null;
		this.updateService.checkUpdatesForUI();
	}

	public itemEditDone(propertyName: string): void {
		this.isEditingProperty[propertyName] = false;
		this.reviewEditsService.currentEditItemProperty = null;
	}

	public editPartner(event, partnerId: string) {
		this.consumeEvent(event);
		const partnerToEdit = this.asset.deliverablePartners.find(
			(p) => p.partnerId === partnerId
		);

		if (partnerToEdit.isDeleted) {
			return;
		}

		this.partnerToEdit = partnerId;
	}

	public isEditingPartner(partnerId: string): boolean {
		return partnerId === this.partnerToEdit;
	}

	public isPartnerDeleted(partnerId: string): boolean {
		return this.findPartner(partnerId).isDeleted;
	}

	public consumeEvent(ev: UIEvent): void {
		ev?.preventDefault();
		ev?.stopPropagation();
	}

	public addPartner(): void {
		const id = `${NewItemPrefix}${uuidv4()}`;
		this.asset.deliverablePartners.push({
			modelId: id, // ADJQ-1934 - Validate this is in line with vw_all_nav_asset_partners when that has data
			assetId: this.asset.assetId,
			partnerId: id,
			role: this.roles[0],
			partnerName: `Unknown`,
		});
		this.updateAsset();
	}

	public togglePartnerDeletion(event: UIEvent, partnerId: string) {
		this.consumeEvent(event);
		if (partnerId.startsWith(NewItemPrefix)) {
			this.asset.deliverablePartners =
				this.asset.deliverablePartners.filter(
					(p) => p.partnerId !== partnerId
				);
		} else {
			const partnerToEdit = this.findPartner(partnerId);
			partnerToEdit.isDeleted = !partnerToEdit.isDeleted;
		}
		this.updateAsset();
	}

	public get roles() {
		return this._deploymentContext.referenceValueData.partnerRole.map(
			(status) => status.value
		);
	}

	public removeOrRestoreLabel(isDeleted) {
		return this.Loc(
			isDeleted ? LocalizedTextIds.Restore : LocalizedTextIds.Remove
		);
	}

	public findPartner(partnerId: string): DeliverablePartner {
		return this.asset.deliverablePartners.find(
			(p) => p.partnerId === partnerId
		);
	}
}
